// =====================================================================
// La file d'attente hors ligne
// =====================================================================
// La collecte se fait au village, sans réseau. On déclare quinze
// personnes assis sous un arbre, et tout part quand on repasse devant une
// antenne.
//
// POURQUOI INDEXEDDB ET PAS localStorage. Deux raisons, dont une
// décisive : localStorage est synchrone — chaque écriture bloque le fil
// d'affichage, ce qui se voit sur les téléphones d'entrée de gamme qui
// sont justement la cible ; et il plafonne autour de cinq méga-octets,
// ce qui suffit pour des déclarations mais pas pour les enregistrements
// audio, qui partagent cette même base.
//
// CHAQUE ENTRÉE PORTE SA CLÉ, tirée une seule fois, à la mise en file.
// Elle accompagne tous les envois et permet au serveur de reconnaître un
// rejeu. Sans elle, une connexion qui tombe après l'écriture mais avant
// la réponse crée un deuxième grand-père à chaque reprise.
// =====================================================================

import type { Identite, RoleParent } from '../partage/types';

const BASE = 'yambi';
const VERSION = 2;
export const FILE = 'file-attente';
/** Les récits enregistrés au village : mêmes règles, autres volumes. */
export const RECITS = 'recits-attente';

/** Une déclaration en attente d'envoi. */
export type Declaration =
  | { genre: 'parent'; role: RoleParent; identite: Identite }
  | {
      genre: 'grand-parent';
      cote: RoleParent;
      role: RoleParent;
      identite: Identite;
    }
  | {
      genre: 'fratrie';
      memes_parents: boolean;
      parent_partage: RoleParent | null;
      identite: Identite;
    }
  | { genre: 'enfant'; identite: Identite };

export interface EnAttente {
  /** Rang d'insertion, attribué par IndexedDB. C'est LUI qui fixe l'ordre
   *  d'envoi : l'horodatage ne suffit pas, quatre déclarations saisies
   *  dans la même milliseconde porteraient la même date et partiraient
   *  dans un ordre arbitraire — un grand-père avant son fils. */
  rang?: number;
  /** Clé d'idempotence ET identifiant provisoire du nœud dans l'arbre. */
  cle: string;
  declaration: Declaration;
  cree_le: string;
  tentatives: number;
  /** Message du serveur quand l'envoi a été refusé pour une raison
   *  métier — un père déjà déclaré, par exemple. */
  erreur: string | null;
}

let base: Promise<IDBDatabase> | null = null;

export function ouvrir(): Promise<IDBDatabase> {
  if (base) return base;

  base = new Promise((resolve, rejeter) => {
    const requete = indexedDB.open(BASE, VERSION);

    requete.onupgradeneeded = () => {
      const db = requete.result;
      // Clé auto-incrémentée : `getAll` rend alors les entrées dans
      // l'ordre où elles ont été saisies, qui est l'ordre d'envoi.
      for (const nom of [FILE, RECITS]) {
        if (db.objectStoreNames.contains(nom)) continue;
        const magasin = db.createObjectStore(nom, {
          keyPath: 'rang',
          autoIncrement: true,
        });
        magasin.createIndex('cle', 'cle', { unique: true });
      }
    };

    requete.onsuccess = () => resolve(requete.result);
    requete.onerror = () => rejeter(requete.error);
  });

  return base;
}

/** Enveloppe une transaction IndexedDB dans une promesse. */
export async function transaction<T>(
  magasins: string | string[],
  mode: IDBTransactionMode,
  travail: (t: IDBTransaction) => IDBRequest<T> | Promise<T>,
): Promise<T> {
  const db = await ouvrir();
  return new Promise<T>((resolve, rejeter) => {
    const t = db.transaction(magasins, mode);
    let valeur: T;
    let attendu: IDBRequest<T> | null = null;

    try {
      const r = travail(t);
      if (r instanceof IDBRequest) {
        attendu = r;
        r.onsuccess = () => {
          valeur = r.result;
        };
      } else {
        void r.then((v) => {
          valeur = v;
        });
      }
    } catch (e) {
      rejeter(e);
      return;
    }

    t.oncomplete = () => resolve(valeur);
    t.onerror = () => rejeter(attendu?.error ?? t.error);
    t.onabort = () => rejeter(t.error);
  });
}

/** Met une déclaration en file et renvoie son identifiant provisoire. */
export async function empiler(declaration: Declaration): Promise<string> {
  const entree: EnAttente = {
    cle: crypto.randomUUID(),
    declaration,
    cree_le: new Date().toISOString(),
    tentatives: 0,
    erreur: null,
  };

  await transaction(FILE, 'readwrite', (t) =>
    t.objectStore(FILE).add(entree),
  );
  previenir();
  return entree.cle;
}

/** Les entrées dans l'ordre où elles ont été saisies. L'ordre compte :
 *  on ne déclare pas un grand-père avant le parent qui y mène. */
export async function lister(): Promise<EnAttente[]> {
  return transaction<EnAttente[]>(FILE, 'readonly', (t) =>
    t.objectStore(FILE).getAll(),
  );
}

export async function retirer(cle: string, magasin = FILE): Promise<void> {
  await surEntree(magasin, cle, (entree, m) => {
    m.delete(entree.rang!);
  });
  previenir();
}

/** Vide entièrement les deux files. Appelé à la déconnexion : les
 *  déclarations et récits non envoyés ne doivent pas partir sous le
 *  compte du prochain utilisateur de l'appareil. */
export async function viderFile(): Promise<void> {
  await transaction([FILE, RECITS], 'readwrite', (t) => {
    t.objectStore(FILE).clear();
    t.objectStore(RECITS).clear();
    return Promise.resolve(undefined);
  });
  previenir();
}

/** Note l'échec sans retirer l'entrée : elle reste visible et rejouable.
 *  Un motif nul l'efface — c'est ce que fait un réessai manuel. */
export async function marquerEchec(
  cle: string,
  erreur: string | null,
  magasin = FILE,
): Promise<void> {
  await surEntree(magasin, cle, (entree, m) => {
    if (erreur !== null) entree.tentatives += 1;
    entree.erreur = erreur;
    m.put(entree);
  });
  previenir();
}

/** Retrouve une entrée par sa clé — l'index — et agit dessus. */
function surEntree<
  E extends { rang?: number; tentatives: number; erreur: string | null },
>(
  nom: string,
  cle: string,
  agir: (entree: E, magasin: IDBObjectStore) => void,
): Promise<void> {
  return transaction(nom, 'readwrite', async (t) => {
    const magasin = t.objectStore(nom);
    const lu = magasin.index('cle').get(cle);
    await new Promise<void>((resolve) => {
      lu.onsuccess = () => {
        const entree = lu.result as E | undefined;
        if (entree) agir(entree, magasin);
        resolve();
      };
    });
  });
}

// =====================================================================
// Les récits enregistrés au village
// =====================================================================
// Même file, mêmes règles, autre magasin : un récit pèse mille fois le
// poids d'une déclaration, et on ne veut pas qu'un envoi de deux
// méga-octets retienne quatorze déclarations de deux cents octets.

export interface RecitEnAttente {
  rang?: number;
  cle: string;
  personne_id: string;
  /** Le nom de la personne, pour pouvoir l'afficher sans réseau. */
  personne_nom: string;
  titre: string | null;
  duree_s: number;
  son: Blob;
  cree_le: string;
  tentatives: number;
  erreur: string | null;
}

export async function empilerRecit(
  r: Omit<RecitEnAttente, 'cle' | 'cree_le' | 'tentatives' | 'erreur' | 'rang'>,
): Promise<string> {
  const entree: RecitEnAttente = {
    ...r,
    cle: crypto.randomUUID(),
    cree_le: new Date().toISOString(),
    tentatives: 0,
    erreur: null,
  };

  await transaction(RECITS, 'readwrite', (t) =>
    t.objectStore(RECITS).add(entree),
  );
  previenir();
  return entree.cle;
}

export function listerRecits(): Promise<RecitEnAttente[]> {
  return transaction<RecitEnAttente[]>(RECITS, 'readonly', (t) =>
    t.objectStore(RECITS).getAll(),
  );
}

// =====================================================================
// Prévenir l'application
// =====================================================================
// IndexedDB n'émet rien quand son contenu change. Sans ce petit signal,
// le compteur de déclarations en attente ne se rafraîchirait qu'au
// prochain rendu — et l'utilisateur ne verrait pas sa file se vider.

const CHANGEMENT = 'yambi:file';

function previenir() {
  window.dispatchEvent(new CustomEvent(CHANGEMENT));
}

export function surChangement(reaction: () => void): () => void {
  window.addEventListener(CHANGEMENT, reaction);
  return () => window.removeEventListener(CHANGEMENT, reaction);
}
